/**
 * EvaluationCriteriaModals - Modal Management for Evaluation Criteria Collections
 *
 * This module provides a React Context-based solution for managing modals
 * related to collections of evaluation criteria on scenarios and rubrics.
 *
 * ## Why This Pattern?
 *
 * 1. **Entity-Level Operations**: Unlike item-level modals, these handle
 *    operations on entire criteria collections (add, sort, import, clear).
 *
 * 2. **Cross-Entity Support**: The same operations work for both scenarios
 *    and rubrics, with entity-specific handling abstracted away.
 *
 * 3. **Complex Import Flow**: The import operation requires its own multi-step
 *    dialog with search, selection, and confirmation.
 *
 * 4. **Bulk Operations**: Actions like "Remove All" need confirmation dialogs
 *    and careful error handling.
 *
 * ## Architecture Overview
 *
 * ```
 * EvaluationCriteriaModalsProvider (entity-level state & modals)
 *     ├── EvaluationCriteriaToolbar
 *     │   └── Buttons → useContext → handleAdd(), handleSort(), etc.
 *     ├── Multiple EvaluationCriteriaCards (each with their own provider)
 *     └── Entity-Level Modals:
 *         ├── AddCriterionDialog
 *         ├── SortCriteriaDialog
 *         ├── ImportFromRubricDialog
 *         └── ConfirmDialog (Remove All)
 * ```
 *
 * ## Usage Example
 *
 * ```tsx
 * // For a scenario
 * <EvaluationCriteriaModalsProvider
 *   entityType="scenario"
 *   scenarioId={scenarioId}
 *   criteria={criteria}
 *   refetch={refetch}
 * >
 *   <EvaluationCriteriaToolbar criteria={criteria} />
 *   {criteria.map(criterion => <EvaluationCriteriaCard {...} />)}
 * </EvaluationCriteriaModalsProvider>
 *
 * // In the toolbar component
 * const { handleAdd, handleSort } = useEvaluationCriteriaModalsContext();
 * ```
 */

import React, { createContext, useCallback, useContext, useState } from "react";

import { ConfirmDialog } from "@/components/pieces/dialog/ConfirmDialog";
import {
  EvaluationCriterionFieldsFragment,
  RubricDetailQuery,
  TargetType,
  useCreateEvaluationCriterionMutation,
  useRemoveAllEvaluationCriteriaFromTargetMutation,
  useReorderEvaluationCriteriaMutation,
} from "@/graphql";
import { NonNullableArrayItem } from "@/types/utils";
import { formatErrors } from "@/utils/array";

import { AddCriterionDialog } from "./dialogs/AddCriterionDialog";
import { SortCriteriaDialog } from "./dialogs/SortCriteriaDialog";
import { ImportFromRubricDialog } from "./import-from-rubric";

type EvaluationCriteriaDialog =
  | "none"
  | "add-criterion"
  | "sort-criteria"
  | "import-from-rubric"
  | "confirm-remove-all-criteria";

// ============================================================================
// Context Types
// ============================================================================

interface EvaluationCriteriaModalsContextValue {
  handleSort: () => void;
  handleAdd: () => void;
  handleImport: () => void;
  handleRemoveAll: () => void;
}

// ============================================================================
// Context Creation
// ============================================================================

const EvaluationCriteriaModalsContext =
  createContext<EvaluationCriteriaModalsContextValue | null>(null);

// ============================================================================
// Custom Hook - Core Modal Logic
// ============================================================================

interface UseEvaluationCriteriaModalsProps {
  entityType: "scenario" | "rubric";
  scenarioId?: string;
  rubricId?: string;
  refetch: () => void;
}

/**
 * Custom hook that manages modals for evaluation criteria collections.
 * Handles add, sort, import, and remove all operations.
 *
 * This hook differs from useEvaluationCriteriaItemModals in that it:
 * - Operates at the collection level, not individual items
 * - Supports both scenarios and rubrics through entityType
 * - Manages more complex operations like import from rubric
 */
function useEvaluationCriteriaModals({
  entityType,
  scenarioId,
  rubricId,
  refetch,
}: UseEvaluationCriteriaModalsProps) {
  // Modal state
  const [activeDialog, setActiveDialog] =
    useState<EvaluationCriteriaDialog>("none");
  const [errMsg, setErrMsg] = useState("");

  // ============================================================================
  // Dialog Control
  // ============================================================================

  const openDialog = useCallback((dialog: EvaluationCriteriaDialog) => {
    setActiveDialog(dialog);
    setErrMsg("");
  }, []);

  const closeDialog = useCallback(() => {
    setActiveDialog("none");
    setErrMsg("");
  }, []);

  // ============================================================================
  // GraphQL Mutations
  // ============================================================================

  /**
   * Create a new evaluation criterion
   */
  const [createCriterion, { loading: createCriterionLoading }] =
    useCreateEvaluationCriterionMutation({
      onCompleted: () => {
        closeDialog();
        refetch();
      },
      onError: (error) => {
        setErrMsg(error.message);
      },
    });

  /**
   * Reorder existing criteria
   */
  const [reorderCriteria, { loading: reorderCriteriaLoading }] =
    useReorderEvaluationCriteriaMutation({
      onCompleted: (data) => {
        if (data?.reorderEvaluationCriteria?.ok) {
          closeDialog();
          refetch();
        } else {
          setErrMsg("Failed to reorder criteria");
        }
      },
      onError: () => {
        setErrMsg("Failed to reorder criteria");
      },
    });

  /**
   * Remove all criteria from the target entity
   */
  const [removeAllCriteria] =
    useRemoveAllEvaluationCriteriaFromTargetMutation();

  // ============================================================================
  // Action Handlers - Open Modals
  // ============================================================================

  const handleSort = useCallback(() => {
    openDialog("sort-criteria");
  }, [openDialog]);

  const handleAdd = useCallback(() => {
    openDialog("add-criterion");
  }, [openDialog]);

  const handleImport = useCallback(() => {
    openDialog("import-from-rubric");
  }, [openDialog]);

  const handleRemoveAll = useCallback(() => {
    openDialog("confirm-remove-all-criteria");
  }, [openDialog]);

  // ============================================================================
  // Save Handlers - Process Modal Actions
  // ============================================================================

  /**
   * Save reordered criteria.
   * Maps the full criterion objects to just id/order pairs for the mutation.
   */
  const onSaveReorderedCriteria = useCallback(
    (orderedCriteria: EvaluationCriterionFieldsFragment[]) => {
      reorderCriteria({
        variables: {
          items: orderedCriteria.map((c) => ({
            id: c.id,
            order: c.order,
          })),
          scenarioId,
          rubricId,
        },
      });
    },
    [reorderCriteria, scenarioId, rubricId],
  );

  /**
   * Create a new criterion with the given name.
   * Automatically determines target type and ID based on entityType.
   */
  const onSaveNewCriterion = useCallback(
    (name: string) => {
      const targetType =
        entityType === "rubric" ? TargetType.Rubric : TargetType.Scenario;
      const targetId = entityType === "rubric" ? rubricId : scenarioId;

      createCriterion({
        variables: {
          targetType,
          targetId: targetId ?? "",
          name,
        },
      });
    },
    [createCriterion, entityType, scenarioId, rubricId],
  );

  /**
   * Helper object for import operations.
   * Provides a consistent interface for target identification.
   */
  const target = {
    type: entityType === "rubric" ? TargetType.Rubric : TargetType.Scenario,
    id: entityType === "rubric" ? (rubricId ?? "") : (scenarioId ?? ""),
  };

  /**
   * Handle confirmed removal of all criteria.
   * This is called after the user confirms in the dialog.
   */
  const handleRemoveAllConfirmed = useCallback(() => {
    removeAllCriteria({
      variables: {
        targetId: target.id,
        targetType: target.type,
      },
      onCompleted: ({ removeAllEvaluationCriteriaFromTarget }) => {
        if (
          removeAllEvaluationCriteriaFromTarget &&
          !removeAllEvaluationCriteriaFromTarget.ok
        ) {
          const errorMsg = formatErrors(
            removeAllEvaluationCriteriaFromTarget.errors,
          );
          setErrMsg(errorMsg);
        } else {
          closeDialog();
          refetch();
        }
      },
      onError: (error) => {
        setErrMsg(error.message);
      },
    });
  }, [removeAllCriteria, target, refetch, closeDialog]);

  return {
    // State
    activeDialog,
    errMsg,
    createCriterionLoading,
    reorderCriteriaLoading,

    // Dialog control
    openDialog,
    closeDialog,

    // Action handlers
    handleSort,
    handleAdd,
    handleImport,
    handleRemoveAll,

    // Save handlers
    onSaveReorderedCriteria,
    onSaveNewCriterion,
    handleRemoveAllConfirmed,

    // Other
    target,
  };
}

// ============================================================================
// Provider Component
// ============================================================================

type Criteria = NonNullableArrayItem<
  NonNullable<RubricDetailQuery["rubric"]>["evaluationCriteria"]
>;

interface EvaluationCriteriaModalsProviderProps {
  criteria: Criteria[];
  refetch: () => void;
  children: React.ReactNode;
  entityType: "scenario" | "rubric";
  scenarioId?: string;
  rubricId?: string;
}

/**
 * Provider component that manages modals for evaluation criteria collections.
 *
 * This provider handles entity-level operations (add, sort, import, clear all)
 * as opposed to item-level operations (edit item, delete item).
 *
 * Supports both scenarios and rubrics through the entityType prop.
 */
export function EvaluationCriteriaModalsProvider({
  entityType,
  scenarioId,
  rubricId,
  refetch,
  children,
  criteria,
}: EvaluationCriteriaModalsProviderProps) {
  const {
    activeDialog,
    errMsg,
    createCriterionLoading,
    reorderCriteriaLoading,
    closeDialog,
    handleSort,
    handleAdd,
    handleImport,
    handleRemoveAll,
    onSaveReorderedCriteria,
    onSaveNewCriterion,
    handleRemoveAllConfirmed,
    target,
  } = useEvaluationCriteriaModals({
    entityType: entityType,
    scenarioId: scenarioId ?? "",
    rubricId: rubricId ?? "",
    refetch: refetch,
  });

  // Context value exposes only the action triggers
  const contextValue: EvaluationCriteriaModalsContextValue = {
    handleSort,
    handleAdd,
    handleImport,
    handleRemoveAll,
  };

  return (
    <EvaluationCriteriaModalsContext.Provider value={contextValue}>
      {children}

      {/* Entity-level modals rendered at provider level */}

      {/* Sort existing criteria */}
      <SortCriteriaDialog
        open={activeDialog === "sort-criteria"}
        onClose={closeDialog}
        onSave={onSaveReorderedCriteria}
        criteria={criteria}
        loading={reorderCriteriaLoading}
        errorMessage={errMsg}
      />

      {/* Add new criterion */}
      <AddCriterionDialog
        open={activeDialog === "add-criterion"}
        onClose={closeDialog}
        onSave={onSaveNewCriterion}
        loading={createCriterionLoading}
        errorMessage={errMsg}
      />

      {/* Import from rubric - complex multi-step dialog */}
      <ImportFromRubricDialog
        open={activeDialog === "import-from-rubric"}
        onClose={closeDialog}
        onSuccess={refetch}
        target={target}
      />

      {/* Confirm remove all - destructive action needs confirmation */}
      {activeDialog === "confirm-remove-all-criteria" && (
        <ConfirmDialog
          onCancel={closeDialog}
          onConfirm={handleRemoveAllConfirmed}
          confirmLabel="Remove All"
          cancelLabel="Nevermind"
        >
          <p>
            Are you sure you want to remove all criteria from this {entityType}?
          </p>
        </ConfirmDialog>
      )}
    </EvaluationCriteriaModalsContext.Provider>
  );
}

// ============================================================================
// Context Consumer Hook
// ============================================================================

/**
 * Hook to access entity-level modal controls from child components.
 * Used primarily by the EvaluationCriteriaToolbar component.
 */
export function useEvaluationCriteriaModalsContext() {
  const context = useContext(EvaluationCriteriaModalsContext);
  if (!context) {
    throw new Error(
      "useEvaluationCriteriaModalsContext must be used within EvaluationCriteriaModalsProvider",
    );
  }
  return context;
}
